<template>
  <section id="about" class="section about" aria-labelledby="about-title">
    <div class="container">
      <h2 id="about-title" class="title">{{ t('about.title') }}</h2>
      <p class="subtitle">{{ t('about.subtitle') }}</p>

      <div class="about-grid">
        <!-- Map on the left -->
        <div class="map" aria-label="Map showing office location">
          <iframe
            class="map-frame"
            title="Office location map"
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
            :src="mapEmbedUrl">
          </iframe>
          <a class="map-link" :href="mapLinkUrl" target="_blank" rel="noopener">Open larger map</a>
        </div>

        <!-- Details on the right -->
        <div class="details">
          <ul class="info-list" aria-describedby="about-title">
            <li class="info-item">
              <div class="badge" aria-hidden="true">@</div>
              <div class="info">
                <div class="label">{{ t('about.emailLabel') }}</div>
                <a class="value link" :href="'mailto:' + t('about.email')">{{ t('about.email') }}</a>
              </div>
            </li>
            <li class="info-item">
              <div class="badge" aria-hidden="true">☎</div>
              <div class="info">
                <div class="label">{{ t('about.phoneLabel') }}</div>
                <a class="value link" :href="'tel:' + t('about.phone').replace(/\s/g, '')">{{ t('about.phone') }}</a>
              </div>
            </li>
            <li class="info-item">
              <div class="badge" aria-hidden="true">⏰</div>
              <div class="info">
                <div class="label">{{ t('about.hoursLabel') }}</div>
                <div class="value">{{ t('about.hours') }}</div>
              </div>
            </li>
            <li class="info-item">
              <div class="badge" aria-hidden="true">⌂</div>
              <div class="info">
                <div class="label">{{ t('about.officeLabel') }}</div>
                <div class="value">{{ t('about.office') }}</div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { i18n } from '@/i18n'

const { t, tm } = useI18n()

// Generate map URLs dynamically from i18n coordinates
// Always use Chinese coordinates regardless of current locale
const mapCoords = computed(() => {
  const messages = tm('about.mapCoords')
  // If current locale doesn't have mapCoords, fallback to zh-CN
  if (!messages || !messages.lat) {
    return i18n.global.messages.value['zh-CN'].about.mapCoords
  }
  return messages
})

const mapEmbedUrl = computed(() => {
  const coords = mapCoords.value
  const lat = coords.lat
  const lon = coords.lon
  const zoom = coords.zoom

  // Calculate delta based on zoom level
  const delta = 0.005 + zoom * 0.001
  const minLon = lon - delta
  const minLat = lat - delta
  const maxLon = lon + delta
  const maxLat = lat + delta

  return `https://www.openstreetmap.org/export/embed.html?bbox=${minLon}%2C${minLat}%2C${maxLon}%2C${maxLat}&layer=mapnik&marker=${lat}%2C${lon}`
})

const mapLinkUrl = computed(() => {
  const coords = mapCoords.value
  const lat = coords.lat
  const lon = coords.lon
  const zoom = coords.zoom

  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=${zoom}/${lat}/${lon}`
})
</script>

<style scoped>
.section.about {
  position: relative; z-index: 0; overflow: hidden;
  padding: 4.5rem 1.5rem 5rem;
  background:
    radial-gradient(1200px 600px at 0% 0%, rgba(96,165,250,0.10), transparent),
    radial-gradient(900px 500px at 100% 100%, rgba(37,99,235,0.12), transparent),
    linear-gradient(180deg, rgba(8,12,24,1) 0%, rgba(10,14,28,1) 100%);
}
.section.about::before {
  content: '';
  position: absolute; inset: -28%; z-index: -1; pointer-events: none;
  background: radial-gradient(45% 55% at 10% 15%, rgba(96,165,250,0.30), transparent 60%),
              radial-gradient(45% 55% at 90% 85%, rgba(37,99,235,0.30), transparent 60%),
              radial-gradient(40% 50% at 60% 50%, rgba(59,130,246,0.22), transparent 60%);
  animation: aurora 20s ease-in-out infinite alternate;
  mix-blend-mode: screen;
}
.container { max-width: 1000px; margin: 0 auto; }
.title { color: #e6f0ff; font-size: clamp(1.6rem, 4vw, 2.2rem); text-align: center; }
.subtitle { color: #9fb0ff; text-align: center; margin-bottom: 1.25rem; }

/* Layout */
.about-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.1rem;
  align-items: start;
}
@media (min-width: 820px) {
  .about-grid { grid-template-columns: 1.1fr 1fr; }
}

.map {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  box-shadow: 0 10px 30px rgba(0,0,0,0.25), inset 0 0 0 1px rgba(255,255,255,0.02);
}
.map::before {
  content: '';
  position: absolute; inset: -1px; pointer-events: none;
  background: conic-gradient(from 0deg, hsla(210,90%,60%,.0), hsla(210,90%,60%,.35), hsla(210,90%,60%,.0));
  filter: blur(18px); opacity: .35;
}
.map-frame {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 12;
  border: 0;
}
.map-link {
  position: absolute; right: .6rem; bottom: .6rem;
  background: rgba(0,0,0,0.45);
  color: #cfe0ff;
  text-decoration: none;
  padding: .25rem .5rem;
  border-radius: 8px;
  font-size: .8rem;
  border: 1px solid rgba(255,255,255,0.18);
}
.map-link:hover { color: #fff; }

/* Details */
.info-list { list-style: none; padding: 0; margin: 0; display: grid; gap: .9rem; }
.info-item {
  display: grid; grid-template-columns: auto 1fr; align-items: center; gap: .9rem;
  padding: 1rem; border-radius: 14px;
  background: linear-gradient(180deg, rgba(18,22,40,0.85), rgba(14,18,34,0.85));
  border: 1px solid rgba(255,255,255,0.08);
}
.badge {
  width: 40px; height: 40px; border-radius: 10px;
  display: grid; place-items: center;
  background: linear-gradient(135deg,#60a5fa,#2563eb);
  color: white; font-weight: 700; font-size: .95rem;
  box-shadow: 0 10px 22px rgba(59, 130, 246, .25);
}
.label { color: #cfe0ff; font-size: .9rem; }
.value { color: #eaf1ff; font-size: 1.05rem; }
.link { color: #9ed0ff; text-decoration: none; }
.link:hover { text-decoration: underline; }

@keyframes aurora {
  0% { transform: translate3d(-2%, -1%, 0) scale(1.04); filter: hue-rotate(0deg) saturate(1.15); }
  50% { transform: translate3d(2%, 1.5%, 0) scale(1.07); filter: hue-rotate(20deg) saturate(1.2); }
  100% { transform: translate3d(-1%, 1%, 0) scale(1.02); filter: hue-rotate(-10deg) saturate(1.15); }
}
@media (prefers-reduced-motion: reduce) {
  .section.about::before { animation: none; }
}

@media (prefers-reduced-motion: no-preference) {
  .badge { transition: transform .25s ease; }
  .info-item:hover .badge { transform: translateY(-2px) scale(1.03); }
}
</style>
